import React, { useRef, useCallback, useState, useEffect } from 'react';
import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { DraggableNode, NodeData } from './DraggableNode';
import { ConnectionLines } from './ConnectionLines';

export interface CanvasNode extends NodeData {
  x: number;
  y: number;
}

export interface Connection {
  from: string; // node id
  to: string;   // node id
}

export interface ValidationIssue {
  type: 'error' | 'warning';
  nodeId?: string;
  connectionKey?: string;
  message: string;
}

interface AutomationCanvasProps {
  nodes: CanvasNode[];
  connections: Connection[];
  onChange: (nodes: CanvasNode[]) => void;
  onConnectionsChange: (connections: Connection[]) => void;
  isActive?: boolean;
}

export function validateWorkflow(nodes: CanvasNode[], connections: Connection[]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const hasTrigger = nodes.some(n => n.type === 'trigger');
  const hasAction = nodes.some(n => n.type === 'action');

  if (!hasTrigger) issues.push({ type: 'error', message: 'AUTOMATION.VALIDATION.NO_TRIGGER' });
  if (!hasAction) issues.push({ type: 'error', message: 'AUTOMATION.VALIDATION.NO_ACTION' });

  // Check orphan nodes (no connections in or out, except triggers can have no input and actions can have no output)
  nodes.forEach(node => {
    const hasOutgoing = connections.some(c => c.from === node.id);
    const hasIncoming = connections.some(c => c.to === node.id);

    if (node.type !== 'action' && !hasOutgoing && nodes.length > 1) {
      issues.push({ type: 'warning', nodeId: node.id, message: 'AUTOMATION.VALIDATION.NO_OUTPUT' });
    }
    if (node.type !== 'trigger' && !hasIncoming && nodes.length > 1) {
      issues.push({ type: 'warning', nodeId: node.id, message: 'AUTOMATION.VALIDATION.NO_INPUT' });
    }
  });

  // Check invalid connections (e.g. action → trigger)
  connections.forEach(conn => {
    const from = nodes.find(n => n.id === conn.from);
    const to = nodes.find(n => n.id === conn.to);
    if (from && to) {
      if (to.type === 'trigger') {
        issues.push({ type: 'error', connectionKey: `${conn.from}-${conn.to}`, message: 'AUTOMATION.VALIDATION.CANT_CONNECT_TO_TRIGGER' });
      }
      if (from.type === 'action') {
        issues.push({ type: 'error', connectionKey: `${conn.from}-${conn.to}`, message: 'AUTOMATION.VALIDATION.CANT_CONNECT_FROM_ACTION' });
      }
    }
  });

  // Check cycles (simple DFS)
  const adjacency: Record<string, string[]> = {};
  connections.forEach(c => {
    if (!adjacency[c.from]) adjacency[c.from] = [];
    adjacency[c.from].push(c.to);
  });
  const visited = new Set<string>();
  const stack = new Set<string>();
  const hasCycle = (nodeId: string): boolean => {
    if (stack.has(nodeId)) return true;
    if (visited.has(nodeId)) return false;
    visited.add(nodeId);
    stack.add(nodeId);
    for (const next of adjacency[nodeId] || []) {
      if (hasCycle(next)) return true;
    }
    stack.delete(nodeId);
    return false;
  };
  for (const node of nodes) {
    if (hasCycle(node.id)) {
      issues.push({ type: 'error', message: 'AUTOMATION.VALIDATION.CYCLE_DETECTED' });
      break;
    }
  }

  return issues;
}

export const AutomationCanvas: React.FC<AutomationCanvasProps> = ({ nodes, connections, onChange, onConnectionsChange, isActive = false }) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLDivElement>(null);
  const [, forceRender] = useState(0);

  // Use a ref for node positions during drag — avoids React re-render per pixel
  const nodesRef = useRef<CanvasNode[]>(nodes);
  useEffect(() => { nodesRef.current = nodes; forceRender(n => n + 1); }, [nodes]);

  // Dragging connection state
  const [connectingFrom, setConnectingFrom] = useState<string | null>(null);
  const [connectingMouse, setConnectingMouse] = useState<{ x: number; y: number } | null>(null);

  const currentNodes = nodesRef.current;
  const issues = validateWorkflow(currentNodes, connections);
  const errorNodeIds = new Set(issues.filter(i => i.nodeId).map(i => i.nodeId!));
  const errorConnectionKeys = new Set(issues.filter(i => i.connectionKey).map(i => i.connectionKey!));

  // Drag: update ref directly, use requestAnimationFrame for DOM updates
  const dragFrameRef = useRef<number>();
  const handleNodeDrag = useCallback((nodeId: string, deltaX: number, deltaY: number) => {
    nodesRef.current = nodesRef.current.map(node =>
      node.id === nodeId
        ? { ...node, x: Math.max(0, node.x + deltaX), y: Math.max(0, node.y + deltaY) }
        : node
    );
    // Batch DOM update
    if (dragFrameRef.current) cancelAnimationFrame(dragFrameRef.current);
    dragFrameRef.current = requestAnimationFrame(() => forceRender(n => n + 1));
  }, []);

  const handleNodeDragEnd = useCallback((nodeId: string) => {
    onChange(nodesRef.current);
  }, [onChange]);

  const handleNodeClick = (_nodeId: string) => {};

  const handleConfigChange = (nodeId: string, config: any) => {
    nodesRef.current = nodesRef.current.map(node => node.id === nodeId ? { ...node, config } : node);
    onChange(nodesRef.current);
  };

  const handleDeleteNode = (nodeId: string) => {
    nodesRef.current = nodesRef.current.filter(node => node.id !== nodeId);
    onChange(nodesRef.current);
    onConnectionsChange(connections.filter(c => c.from !== nodeId && c.to !== nodeId));
    forceRender(n => n + 1);
  };

  // Connection dragging
  const handlePortDragStart = (nodeId: string, portType: 'output') => {
    setConnectingFrom(nodeId);
  };

  const handlePortDrop = (nodeId: string, portType: 'input') => {
    if (connectingFrom && connectingFrom !== nodeId) {
      // Check duplicate
      const exists = connections.some(c => c.from === connectingFrom && c.to === nodeId);
      if (!exists) {
        onConnectionsChange([...connections, { from: connectingFrom, to: nodeId }]);
      }
    }
    setConnectingFrom(null);
    setConnectingMouse(null);
  };

  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    if (connectingFrom && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      setConnectingMouse({
        x: e.clientX - rect.left + canvasRef.current.scrollLeft,
        y: e.clientY - rect.top + canvasRef.current.scrollTop,
      });
    }
  };

  const handleCanvasMouseUp = () => {
    setConnectingFrom(null);
    setConnectingMouse(null);
  };

  // Remove a connection
  const handleRemoveConnection = (from: string, to: string) => {
    onConnectionsChange(connections.filter(c => !(c.from === from && c.to === to)));
  };

  return (
    <div
      ref={canvasRef}
      onMouseMove={handleCanvasMouseMove}
      onMouseUp={handleCanvasMouseUp}
      className={cn(
        'relative size-full overflow-auto border bg-muted',
        'bg-[radial-gradient(circle,var(--border)_1px,transparent_1px)] bg-[length:20px_20px]',
        connectingFrom ? 'cursor-crosshair' : 'cursor-default',
      )}
    >
      {/* Inner container that expands to fit all nodes */}
      <div
        className="relative"
        style={{
          minWidth: Math.max(800, ...currentNodes.map(n => n.x + 350)),
          minHeight: Math.max(400, ...currentNodes.map(n => n.y + 200)),
        }}
      >
        {/* Connection Lines */}
        <ConnectionLines
          nodes={currentNodes}
          connections={connections}
          errorConnectionKeys={errorConnectionKeys}
          connectingFrom={connectingFrom}
          connectingMouse={connectingMouse}
          onRemoveConnection={handleRemoveConnection}
          isActive={isActive}
        />

        {/* Nodes */}
        {currentNodes.map(node => (
          <DraggableNode
            key={node.id}
            node={node}
            onDrag={handleNodeDrag}
            onDragEnd={handleNodeDragEnd}
            onClick={handleNodeClick}
            onDelete={handleDeleteNode}
            onConfigChange={handleConfigChange}
            onPortDragStart={handlePortDragStart}
            onPortDrop={handlePortDrop}
            isConnecting={!!connectingFrom}
            hasError={errorNodeIds.has(node.id)}
          />
        ))}

        {/* Empty state — when no nodes */}
        {currentNodes.length === 0 && (
          <div className="pointer-events-none absolute top-1/2 left-1/2 z-[5] flex -translate-x-1/2 -translate-y-1/2 flex-col items-center">
            <CirclePlus className="mb-2 size-10 text-muted-foreground/20" strokeWidth={1.5} />
            <p className="text-sm text-muted-foreground/50">{t('AUTOMATION.CANVAS_EMPTY')}</p>
          </div>
        )}

        {/* Validation issues — only when there are nodes but something is wrong */}
        {currentNodes.length > 0 && issues.filter(i => i.type === 'error' && !i.nodeId && !i.connectionKey).length > 0 && (
          <div className="absolute inset-x-3 bottom-3 z-10 flex flex-wrap justify-center gap-1.5">
            {issues.filter(i => i.type === 'error' && !i.nodeId && !i.connectionKey).map((issue, idx) => (
              <span
                key={idx}
                className="border border-destructive/20 bg-destructive/10 px-3 py-1 text-xs font-medium whitespace-nowrap text-destructive backdrop-blur-sm"
              >
                {t(issue.message)}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
